import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useIsFocused } from '@sg/garnish';

import { useUserLocation } from '@order/shared-hooks';

import { checkIfLateOrder, checkIfPastOrder } from '../../utils';
import { useChallengeCelebration } from '../useChallengeCelebration';
import { useMomoSurveyUrl } from '../useMomoSurveyUrl';
import { useOrderHealthKit } from '../useOrderHealthKit';
import { useOrderStatusPolling } from '../useOrderStatusPolling';
import { useOrderStatusRequest, type UseOrderStatusRequestParams } from '../useOrderStatusRequest';

export function useOrderStatusData(params: UseOrderStatusDataParams) {
  const { isLoggedIn, customer } = params;

  const { formatMessage } = useIntl();
  const isRenderedOnScreen = useIsFocused();

  // ─── Request ─────────────────────────────────────────────────────────

  const requestParams: UseOrderStatusRequestParams = {
    isLoggedIn,
    customerId: customer.id ?? '',
  };

  const response = useOrderStatusRequest(requestParams);

  const {
    order,
    courierDetails,
    orderStatus,
    orderId,
    orderType,
    isFetching: isFetchingOrderStatus,
    refetchCanCancelStatus,
  } = response;

  // ─── Order Status Polling ────────────────────────────────────────────

  const {
    status,
    courier,
    refetchOrderStatus: refetchOrderStatusData,
  } = useOrderStatusPolling(orderId, orderType, orderStatus, courierDetails);

  const refetchOrderStatus = useCallback(() => {
    refetchCanCancelStatus();
    refetchOrderStatusData();
  }, [refetchCanCancelStatus, refetchOrderStatusData]);

  // ─── Derived Statuses ────────────────────────────────────────────────

  const isLate = checkIfLateOrder({ order, flattenedOrderStatus: status });
  const isPast = checkIfPastOrder({ order, flattenedOrderStatus: status });

  // ─── Health Kit ──────────────────────────────────────────────────────

  const { isOrderToHealthKitEnabled, orderToHealthKitLineItems } = useOrderHealthKit(order);

  // ─── User Data ───────────────────────────────────────────────────────

  const customerName = formatMessage(messages.you);
  const userLocationData = useUserLocation({
    pollingInterval: GEO_POLLING_INTERVAL,
  });
  const { geolocation } = userLocationData;
  const { latitude: userLat, longitude: userLon } = geolocation ?? {};

  // ─── Momo Survey ─────────────────────────────────────────────────────

  const momoSurveyUrl = useMomoSurveyUrl(
    isLoggedIn
      ? {
          phoneNumber: customer.phoneNumber,
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
        }
      : undefined,
    {
      orderId,
      orderType,
      orderTime: order?.wantedTime,
      restaurantEntityId: order?.restaurant?.entityId,
    },
  );

  // ─── Challenge Celebration ───────────────────────────────────────────

  const challengeData = useChallengeCelebration(orderId);
  const { celebration, fetching: isFetchingCelebration } = challengeData;

  // ─── Data ────────────────────────────────────────────────────────────

  return {
    ...response,
    isFetching: isFetchingOrderStatus || isFetchingCelebration,

    isRenderedOnScreen,

    status,

    orderToHealthKitLineItems,
    isOrderToHealthKitEnabled,

    isLate,
    isPast,

    customerName,
    customerLatitude: userLat ?? 0,
    customerLongitude: userLon ?? 0,

    courierName: courier?.name,
    courierPhone: courier?.phone,
    courierLatitude: courier?.latitude,
    courierLongitude: courier?.longitude,

    momoSurveyUrl,
    celebration,

    refetchOrderStatus,
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  you: {
    defaultMessage: 'You',
    description: 'Order Status | Status Data Fetch | Customer Name',
  },
});

// ─── Constants ─────────────────────────────────────────────────────────

const GEO_POLLING_INTERVAL = 60 * 1000; // 60 seconds.

// ─── Types ───────────────────────────────────────────────────────────────────

export type UseOrderStatusDataParams = {
  isLoggedIn: boolean;
  customer: OrderStatusDataCustomer;
};

type OrderStatusDataCustomer = {
  id?: string | null;
  phoneNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};
